/**
 * Author: Cascade
 * Date: 2026-01-21
 * PURPOSE: Shared Worm Arena pinned "Greatest Hits" list so replay pages,
 *          cards, and default selection logic can keep keystone matches
 *          (like Grok vs GPT-5.1) surfaced regardless of API rotation.
 *          Also exposes helpers for consumers that need a canonical default
 *          game ID.
 * SRP/DRY check: Pass — centralizes the curated list instead of duplicating
 *          per-component arrays.
 */

import type { WormArenaGreatestHitGame } from '@shared/types';

export const PINNED_WORM_ARENA_GAMES: WormArenaGreatestHitGame[] = [
  {
    gameId: 'c6351f1c-2a3f-4e98-93ab-05e38f06a1c7',
    startedAt: '2026-01-15T01:27:59.164733',
    modelA: 'openai/gpt-5-nano',
    modelB: 'x-ai/grok-code-fast-1',
    roundsPlayed: 44,
    maxRounds: 150,
    totalCost: 0.4072954,
    maxFinalScore: 20,
    scoreDelta: 4,
    boardWidth: 10,
    boardHeight: 10,
    highlightReason:
      'Pinned: Grok Code Fast 1 ended GPT-5 Nano 20-16 after a 44-round duel and simultaneous head collision in round 43.',
  },
  {
    gameId: 'cb60bec2-75b1-4bf9-b868-6fd6ca822956',
    startedAt: '2026-01-13T19:56:44.809932',
    modelA: 'x-ai/grok-code-fast-1',
    modelB: 'openai/gpt-5-mini',
    roundsPlayed: 42,
    maxRounds: 150,
    totalCost: 0.52263015,
    maxFinalScore: 19,
    scoreDelta: 5,
    boardWidth: 10,
    boardHeight: 10,
    highlightReason:
      'Pinned: GPT-5 Mini outlasted Grok Code Fast 1, 19-14, after forcing a body collision in round 41 of a 42-round duel.',
  },
  {
    gameId: '8bca1c80-c63e-4ab5-824b-2a77c5ffee3e',
    startedAt: '2026-01-13T22:06:13.382383',
    modelA: 'openai/gpt-5.1-codex-mini',
    modelB: 'x-ai/grok-code-fast-1',
    roundsPlayed: 42,
    maxRounds: 150,
    totalCost: 0.5118074,
    maxFinalScore: 21,
    scoreDelta: 1,
    boardWidth: 10,
    boardHeight: 10,
    highlightReason:
      'Pinned: Grok Code Fast 1 edged GPT-5.1 Codex Mini 21-20 in a 42-round brawl (dual collisions, clutch finish).',
  },
  {
    gameId: 'd8cd9202-5121-448a-a5bb-194ce5095e5e',
    startedAt: '2026-01-15T04:06:25.629819',
    modelA: 'openai/gpt-oss-120b',
    modelB: 'deepseek/deepseek-v3.2',
    roundsPlayed: 54,
    maxRounds: 150,
    totalCost: 0.09303918,
    maxFinalScore: 20,
    scoreDelta: 3,
    boardWidth: 10,
    boardHeight: 10,
    highlightReason:
      'Pinned: GPT-OSS 120B outlasted DeepSeek V3.2, 20-17, after dual body collisions at round 53 on a 10x10 board.',
  },
  {
    gameId: '42ccab35-b987-425c-8a32-5a9f7040f6aa',
    startedAt: '2026-01-13T22:06:13.382383',
    modelA: 'openai/gpt-5.1-codex-mini',
    modelB: 'x-ai/grok-code-fast-1',
    roundsPlayed: 42,
    maxRounds: 150,
    totalCost: 0.5118074,
    maxFinalScore: 21,
    scoreDelta: 1,
    boardWidth: 10,
    boardHeight: 10,
    highlightReason: 'Pinned: Grok Code Fast 1 edged GPT-5.1 Codex Mini 21-20 in a 42-round match. Codex Mini died via body collision in round 41.',
  },
  {
    gameId: '17b4cccc-e0b2-44a7-bc65-d69a13221993',
    startedAt: '2025-12-26T00:44:49.624264',
    modelA: 'google/gemini-2.5-flash-preview-09-2025',
    modelB: 'deepseek/deepseek-v3.2-exp',
    roundsPlayed: 58,
    maxRounds: 150,
    totalCost: 0.7013982,
    maxFinalScore: 18,
    scoreDelta: 7,
    boardWidth: 10,
    boardHeight: 10,
    highlightReason: 'Pinned: 58-round slugfest, 18-11 finish (Gemini vs DeepSeek).',
  },
  {
    gameId: 'f1b8d1ab-5a62-410d-8e47-d52e27729eb6',
    startedAt: '2025-12-26T00:45:31.741935',
    modelA: 'deepseek/deepseek-v3.2-exp',
    modelB: 'openai/gpt-5-nano',
    roundsPlayed: 77,
    maxRounds: 150,
    totalCost: 0.1382053,
    maxFinalScore: 15,
    scoreDelta: 0,
    boardWidth: 10,
    boardHeight: 10,
    highlightReason: 'Pinned: 77-round tie, dual head-collision at 15-15 (DeepSeek vs GPT-5 Nano).',
  },
  {
    gameId: 'cbb4bc85-5970-4f9e-9335-914e6e3f1091',
    startedAt: '2025-12-26T02:10:00.173382',
    modelA: 'nvidia/nemotron-3-nano-30b-a3b:free',
    modelB: 'openai/gpt-5.1-codex-mini',
    roundsPlayed: 53,
    maxRounds: 150,
    totalCost: 0.117699,
    maxFinalScore: 11,
    scoreDelta: 1,
    boardWidth: 10,
    boardHeight: 10,
    highlightReason: 'Pinned: Nemotron 3 Nano edged GPT-5.1 Codex Mini 11-10 after a 53-round duel.',
  },
  {
    gameId: 'a3f0a2ba-7031-432f-87de-b57cab4623f3',
    startedAt: '2025-12-26T01:59:40.133172',
    modelA: 'openai/gpt-5.1-codex-mini',
    modelB: 'openai/gpt-5-nano',
    roundsPlayed: 90,
    maxRounds: 150,
    totalCost: 0.42447545,
    maxFinalScore: 21,
    scoreDelta: 1,
    boardWidth: 10,
    boardHeight: 10,
    highlightReason: 'Pinned: GPT-5 Nano outlasted GPT-5.1 Codex Mini 21-20 in a 90-round head-collision finish.',
  },
  {
    gameId: '2d061284-49be-44b3-84f1-38339c1f9211',
    startedAt: '2025-12-25T23:42:12.028394',
    modelA: 'x-ai/grok-4.1-fast',
    modelB: 'z-ai/glm-4.7',
    roundsPlayed: 94,
    maxRounds: 150,
    totalCost: 1.7030653,
    maxFinalScore: 24,
    scoreDelta: 3,
    boardWidth: 10,
    boardHeight: 10,
    highlightReason: 'Pinned: Grok 4.1 Fast thrashed GLM 4.7 in a 24-21, 94-round barnburner.',
  },
];

export function getDefaultPinnedWormArenaGameId(): string {
  return PINNED_WORM_ARENA_GAMES[0]?.gameId ?? '';
}
